import * as z from 'zod'
import { TimestampMetadataSchema } from '../_common'
import { DateTimeSchema } from '../datetime'
import { AccountDefaultConfigSchema } from './config'
import { PlanSchema } from '../_plan'

const AccountLimitationSchema = z.object({
  // Limit count can be a positive-number or `null` for unlimited
  branchLimitCount: z.int().min(0).nullable().default(1),
  // Use count must be a positive-number
  branchUseCount: z.int().min(0).default(0),
})

/**
 * [@]: `/accounts/{accountId}`
 */
export const AccountSchema = z.object({
  id: z.string().nonempty(),
  ownerUid: z.string().nonempty(),

  accountName: z.string().nonempty(),

  config: AccountDefaultConfigSchema
    .nullish()
    .transform((v) => v || AccountDefaultConfigSchema.parse({})),

  limits: AccountLimitationSchema,

  ...TimestampMetadataSchema.shape,
})

export type LooseAccount = z.input<typeof AccountSchema>
export type Account = z.output<typeof AccountSchema>

/**
 * [@]: `/accounts/{accountId}/subscriptions/{subscriptionId}`
 */
export const AccountSubscriptionSchema = z.object({
  id: z.string().nonempty(),
  accountId: z.string().nonempty(),
  // Which Branch use this subscription
  branchIds: z.array(z.string().nonempty()),

  planType: PlanSchema, // Supports 'free' | 'standard' | 'enterprise'

  fromPaymentGateway: z.object({
    licenseCode: z.string(),
    status: z.string(),
    transactionId: z.string(),
    productId: z.string(),
    customerId: z.string(),
    customerName: z.string(),
    customerEmail: z.email(),
    membershipTierId: z.string(),
    membershipTierName: z.string(),
    memberPaymentLink: z.url(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    expiredAt: DateTimeSchema,
  }).partial().nullable(), // Optional for free tier

  limits: AccountLimitationSchema,

  // Kapan diaktivasi di aplikasi
  activatedAt: DateTimeSchema,
  activatedBy: z.string(),

  possibleExpiredAt: DateTimeSchema.nullish().transform((v) => v || null),

  // When to start alert soon expiring
  // `null` means don't shows alert.
  startAlertSoonExpiringAt: DateTimeSchema.nullish().transform((v) => v || null),

  ...TimestampMetadataSchema.shape,
}).transform((subscription) => {
  const guessExpiredByActivatedAt = () => {
    if (subscription.activatedAt instanceof Date) {
      const possibleExpiredAt = new Date(subscription.activatedAt)

      // guess by adding 30 days
      possibleExpiredAt.setDate(possibleExpiredAt.getDate() + 30)

      return possibleExpiredAt
    }

    return null
  }

  const possibleExpiredAt = subscription.possibleExpiredAt || subscription.fromPaymentGateway?.expiredAt || guessExpiredByActivatedAt()

  return {
    ...subscription,
    possibleExpiredAt,
  }
})

export type LooseAccountSubscription = z.input<typeof AccountSubscriptionSchema>
export type AccountSubscription = z.output<typeof AccountSubscriptionSchema>
